import React, { useState } from 'react';
import { teacherApi, BulkGoalUploadResult } from '../services/api';

const F = { fontFamily: 'Inter' } as const;

interface Props {
  target: { type: 'class' | 'student'; id: string; name: string };
  onClose: () => void;
  /** Called after a successful apply so the parent can refresh if needed. */
  onDone?: () => void;
}

type Phase = 'idle' | 'previewing' | 'preview' | 'applying' | 'done';

/**
 * Toplu Hedef Atama (.xlsx) — upload a filled study-plan template to bulk-create
 * goals for a whole class or a single student. Two steps: Önizle (dry-run,
 * shows per-student counts + conflicts, no writes) then Uygula (commit).
 */
export default function BulkGoalUploadModal({ target, onClose, onDone }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [result, setResult] = useState<BulkGoalUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errWarnings, setErrWarnings] = useState<string[]>([]);

  const apiTarget =
    target.type === 'class'
      ? ({ targetType: 'class', classId: target.id } as const)
      : ({ targetType: 'student', studentUserId: target.id } as const);

  const busy = phase === 'previewing' || phase === 'applying';

  const downloadTemplate = async () => {
    try {
      const res = await teacherApi.bulkGoalTemplate();
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'calisma-plani-sablonu.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Şablon indirilemedi. Bağlantınızı kontrol edip tekrar deneyin.');
    }
  };

  const run = async (apply: boolean) => {
    if (!file) return;
    setError(null);
    setErrWarnings([]);
    setProgress(0);
    setPhase(apply ? 'applying' : 'previewing');
    try {
      const res = await teacherApi.bulkAssignGoalsUpload(
        apiTarget,
        file,
        { apply, replaceExisting },
        (p) => setProgress(p),
      );
      setResult(res.data);
      setPhase(apply ? 'done' : 'preview');
      if (apply) onDone?.();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Yükleme başarısız');
      setErrWarnings(e?.response?.data?.warnings ?? []);
      setPhase(file ? 'idle' : 'idle');
    }
  };

  const targetLabel = target.type === 'class' ? `Sınıf: ${target.name}` : `Öğrenci: ${target.name}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative bg-white rounded-[20px] w-full max-w-[640px] p-6 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} disabled={busy} className="absolute top-4 right-4 text-[#2b2b2b] text-2xl disabled:opacity-40">×</button>

        <div className="flex items-center gap-3 mb-1">
          <div className="w-[42px] h-[42px] rounded-[12px] bg-[#59e56e]/15 flex items-center justify-center">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#179e3b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="3" width="16" height="18" rx="2" /><line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="8" y1="16" x2="13" y2="16" />
            </svg>
          </div>
          <h2 style={{ ...F, fontSize: 22, fontWeight: 700, color: '#2b2b2b' }}>Toplu Hedef Atama (.xlsx)</h2>
        </div>
        <p style={{ ...F }} className="text-[13px] text-gray-500 mb-4">{targetLabel}</p>

        {/* Step 1: template */}
        <div className="bg-[#f6f7fb] rounded-[14px] p-4 mb-4">
          <p style={{ ...F }} className="text-[13px] text-[#2b2b2b] mb-3">
            Şablonu indirin, her satıra bir hedef (tarih, ders, soru sayısı) girin ve dosyayı yükleyin.
            Bir haftanın tarihlerini doldurursanız o hafta, tüm dönemi doldurursanız her hafta dolar.
          </p>
          <button onClick={downloadTemplate} className="rounded-[12px] px-4 py-2.5 flex items-center gap-2 hover:bg-blue-50" style={{ border: '2px solid #3973bf' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#3973bf' }}>Şablonu İndir</span>
          </button>
        </div>

        {/* Step 2: file */}
        <div className="mb-4">
          <label className="flex items-center justify-between gap-3 rounded-[12px] border border-dashed border-gray-300 px-4 py-3 cursor-pointer hover:border-[#3973bf]">
            <span style={{ ...F }} className="text-[13px] text-[#2b2b2b] truncate">
              {file ? file.name : 'Doldurulmuş .xlsx dosyasını seçin'}
            </span>
            <span style={{ ...F, fontSize: 13, fontWeight: 700 }} className="text-[#3973bf] shrink-0">Dosya Seç</span>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); setErrWarnings([]); setPhase('idle'); }}
            />
          </label>
          <label className="flex items-start gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={replaceExisting}
              onChange={(e) => {
                setReplaceExisting(e.target.checked);
                // A preview was computed for the other choice — its numbers (and the
                // "silinecek" count) no longer apply.
                if (phase === 'preview') { setResult(null); setPhase('idle'); }
              }}
            />
            <span style={{ ...F }} className="text-[12px] text-gray-600">
              Bu tarih aralığında <strong>sizin</strong> atadığınız mevcut hedefleri sil ve yeniden oluştur.
              Diğer öğretmenlerin, kurumun ve öğrencinin kendi hedeflerine dokunulmaz.
            </span>
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-[12px] bg-red-50 border border-red-200 p-3 mb-4">
            <p style={{ ...F }} className="text-[13px] text-red-700 font-semibold">{error}</p>
            {errWarnings.length > 0 && (
              <ul className="mt-2 space-y-0.5 max-h-[160px] overflow-y-auto">
                {errWarnings.map((w, i) => <li key={i} style={{ ...F }} className="text-[12px] text-red-600">⚠ {w}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* Preview / result */}
        {result && (phase === 'preview' || phase === 'done') && (
          <div className={`rounded-[12px] border p-3 mb-4 ${phase === 'done' ? 'bg-[#59e56e]/10 border-[#59e56e]' : 'bg-[#3973bf]/5 border-[#3973bf]/30'}`}>
            <p style={{ ...F }} className="text-[14px] font-bold text-[#2b2b2b] mb-1">
              {phase === 'done'
                ? `✓ ${result.created} hedef oluşturuldu`
                : `Önizleme: ${result.created} hedef oluşturulacak, ${result.skipped} atlanacak`}
            </p>
            <p style={{ ...F }} className="text-[12px] text-gray-600">
              {result.totalRows} satır · {result.studentCount} öğrenci
              {result.replacedExisting > 0
                ? ` · ${result.replacedExisting} eski hedefiniz ${phase === 'done' ? 'silindi' : 'silinecek'}`
                : ''}
              {result.capped ? ` · üst sınıra ulaşıldı (${result.created})` : ''}
            </p>
            {phase === 'preview' && replaceExisting && (
              <p style={{ ...F }} className={`text-[12px] mt-1 ${result.replacedExisting > 0 ? 'text-[#a97e00] font-semibold' : 'text-gray-500'}`}>
                {result.replacedExisting > 0
                  ? `Uygula dediğinizde, sizin atadığınız ${result.replacedExisting} mevcut hedef silinip yerine yenileri oluşturulacak. Diğer öğretmenlerin hedefleri silinmez.`
                  : 'Bu aralıkta sizin atadığınız silinecek bir hedef yok; yeni hedefler eklenecek.'}
              </p>
            )}

            {result.perStudent.length > 1 && (
              <div className="mt-2 max-h-[140px] overflow-y-auto">
                {result.perStudent.map((s) => (
                  <div key={s.studentId} className="flex justify-between text-[12px] py-0.5" style={{ ...F }}>
                    <span className="text-[#2b2b2b] truncate">{s.name}</span>
                    <span className="text-gray-500 shrink-0 ml-2">+{s.create}{s.skip ? ` · ${s.skip} atlandı` : ''}</span>
                  </div>
                ))}
              </div>
            )}

            {result.conflicts.length > 0 && (
              <details className="mt-2">
                <summary style={{ ...F }} className="text-[12px] text-[#a97e00] cursor-pointer">{result.conflicts.length} çakışma/atlanan</summary>
                <ul className="mt-1 space-y-0.5 max-h-[140px] overflow-y-auto">
                  {result.conflicts.map((c, i) => <li key={i} style={{ ...F }} className="text-[12px] text-[#a97e00]">• {c.student}: {c.message}</li>)}
                </ul>
              </details>
            )}

            {result.warnings.length > 0 && (
              <details className="mt-2">
                <summary style={{ ...F }} className="text-[12px] text-gray-500 cursor-pointer">{result.warnings.length} uyarı (atlanan satırlar)</summary>
                <ul className="mt-1 space-y-0.5 max-h-[140px] overflow-y-auto">
                  {result.warnings.map((w, i) => <li key={i} style={{ ...F }} className="text-[12px] text-gray-500">⚠ {w}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        {busy && (
          <p style={{ ...F }} className="text-[13px] text-[#3973bf] mb-3">
            {phase === 'applying' ? 'Oluşturuluyor' : 'Önizleme hazırlanıyor'}… %{progress}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} disabled={busy} className="rounded-[12px] px-5 py-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
            <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>{phase === 'done' ? 'Kapat' : 'Vazgeç'}</span>
          </button>
          {phase !== 'done' && (
            <button onClick={() => run(false)} disabled={!file || busy} className="rounded-[12px] px-5 py-2.5 hover:opacity-90 disabled:opacity-50" style={{ background: '#3973bf' }}>
              <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>Önizle</span>
            </button>
          )}
          {phase === 'preview' && result && result.created > 0 && (
            <button onClick={() => run(true)} disabled={busy} className="rounded-[12px] px-5 py-2.5 hover:opacity-90 disabled:opacity-50" style={{ background: '#59e56e' }}>
              <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fff' }}>Uygula ({result.created})</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
